import React, { useState } from 'react';
import MainLayout from '../components/layout/MainLayout';
import Section from '../components/ui/Section';
import Button from '../components/ui/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';
import { mockResources } from '../data/mockData';

const Newsletter = () => {
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setError(null);
    try {
      const { error: sbError } = await supabase
        .from('newsletter_subscribers_1779580000000')
        .insert([{ full_name: formData.name, email: formData.email, is_verified: false }]);
      if (sbError) throw sbError;
      setStatus('success');
    } catch (err) {
      setError(err.message || 'Something went wrong.');
      setStatus('error');
    }
  };

  // Helper to extract the Framework Letter for the visual badge
  // Handles both hyphens and em-dashes
  const getFrameworkLetter = (title) => {
    const match = title.match(/Part \d+ [-\u2014] ([S|A|F|E|I]) is for/);
    return match ? match[1] : '•';
  };

  return (
    <MainLayout>
      <Section bg="navy" className="pt-24 pb-20 text-center">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">AI Powered & Human Inspired.</h1>
          <p className="text-xl text-gray-300 mb-12">
            A weekly digital journal for nonprofit leaders moving from AI curiosity to organisational confidence.
          </p>
          
          {status === 'success' ? (
            <div className="bg-white/10 backdrop-blur-md p-10 rounded-3xl border border-white/20 animate-fade-in">
              <SafeIcon icon={FiIcons.FiCheckCircle} className="w-12 h-12 text-brand-teal mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">Check your inbox!</h3>
              <p className="text-gray-300">We've sent a verification link to {formData.email}.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="max-w-md mx-auto flex flex-col md:flex-row gap-3">
              <input 
                type="email" 
                required 
                placeholder="Your work email" 
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="flex-grow p-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-gray-400 focus:ring-2 focus:ring-brand-teal outline-none"
              />
              <Button type="submit" variant="primary" className="py-4 px-8 whitespace-nowrap" disabled={status === 'submitting'}>
                {status === 'submitting' ? 'Joining...' : 'Subscribe'}
              </Button>
            </form>
          )}
          {error && <p className="text-red-400 mt-4 text-sm">{error}</p>}
        </div>
      </Section>

      <Section bg="cream" className="py-20">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex items-center justify-between mb-12 border-b border-gray-200 pb-6">
            <div>
              <h2 className="text-2xl font-bold text-brand-navy uppercase tracking-widest text-sm mb-1">The Digital Journal</h2>
              <p className="text-gray-500 text-xs font-medium">LATEST ARTICLES & FRAMEWORK GUIDES</p>
            </div>
            <SafeIcon icon={FiIcons.FiBookOpen} className="text-brand-teal w-6 h-6" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {[...mockResources].reverse().map((article) => (
              <div key={article.id} className="group cursor-pointer" onClick={() => window.location.hash = `/resources/${article.slug}`}>
                <div className="mb-6 overflow-hidden rounded-2xl bg-brand-navy/5 aspect-[16/9] flex items-center justify-center relative">
                   <div className="text-brand-teal/20 font-bold text-8xl group-hover:scale-110 transition-transform duration-500 select-none">
                     {getFrameworkLetter(article.title)}
                   </div>
                   <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[10px] font-bold text-brand-teal uppercase tracking-[0.2em]">{article.category}</span>
                  <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                  <span className="text-[10px] font-medium text-gray-500 uppercase tracking-widest">
                    {new Date(article.created_at).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-brand-navy mb-4 group-hover:text-brand-teal transition-colors leading-tight">
                  {article.title}
                </h3>
                <p className="text-gray-600 mb-6 line-clamp-2 text-sm leading-relaxed">{article.excerpt}</p>
                <div className="flex items-center gap-2 text-brand-navy font-bold text-sm group-hover:gap-3 transition-all">
                  Read Journal Entry <SafeIcon icon={FiIcons.FiArrowRight} className="text-brand-teal" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>
    </MainLayout>
  );
};

export default Newsletter;